import { createContext, useContext } from 'react';
import type { Package, PackageDraft, WarehouseService } from '@mypoz/shared';

/** One goods-type code and its label in the current language — see `PackagesState.goodsTypes`. */
export interface GoodsTypeOption {
  readonly code: string;
  readonly label: string;
}

export interface PackagesState {
  /**
   * Every package on the account, newest first as the vendor orders them. Empty while loading and
   * after a failure — callers branch on `status`, not on the length of this.
   */
  readonly packages: readonly Package[];
  readonly status: 'loading' | 'ready' | 'error';
  /**
   * Whether the status and goods-type dictionaries have loaded (MYP-126). They arrive before the
   * whole list, which the paged Packages screen does not wait for.
   */
  readonly dictionariesStatus: 'loading' | 'ready' | 'error';
  /** Vendor rows from one page of `package_list`, labelled with the dictionaries above. */
  toPackages: (rows: readonly unknown[]) => readonly Package[];
  /**
   * Goes up on every reload, including the one after an add, edit or delete — the paged screen
   * asks for its page again when it changes.
   */
  readonly listVersion: number;
  /**
   * The vendor's goods-type dictionary (MYP-102), labels already resolved to the reader's language.
   * Empty until `status` is `'ready'` — it loads with the list, and fails with it.
   */
  readonly goodsTypes: readonly GoodsTypeOption[];
  /**
   * Whether the warehouse service lists (MYP-100) have loaded. Separate from `status` on purpose:
   * services only matter inside the add/edit sheet, so a failure here degrades that one section
   * rather than taking the whole package list down with it.
   */
  readonly servicesStatus: 'loading' | 'ready' | 'error';
  /**
   * One warehouse's own services, names resolved to the reader's language. Empty while loading,
   * after a failure, and for a warehouse that offers none — check `servicesStatus` to tell those
   * apart.
   */
  servicesFor: (warehouseId: string) => readonly WarehouseService[];
  /** Asks the vendor again. Safe to call at any time; a fetch already in flight is discarded. */
  reload: () => void;
  /** Asks again for the warehouse services only — the package list is left as it is. */
  reloadServices: () => void;
  /**
   * Registers a new package with the vendor (MYP-98), then re-reads the list so it shows up.
   * Rejects with a `PackagesRequestError` carrying the vendor code on failure. When the outcome is
   * uncertain — a half-failure, or no answer at all — the list is re-read anyway, so a package
   * that did get created is visible before the customer tries again.
   */
  addPackage: (draft: PackageDraft) => Promise<void>;
  /**
   * Deletes one package with the vendor (MYP-99), then re-reads the list so it is gone. Rejects
   * with a `PackagesRequestError` on failure; the list is re-read then too unless the vendor
   * refused cleanly (0, 2, -2), since a lost answer may mean the delete did happen.
   */
  deletePackage: (packageId: string) => Promise<void>;
  /**
   * Saves an edit to an existing package (MYP-123) through `add_packages`, which updates when the
   * tracking number already exists (vendor's answer to question 7). Then re-reads the list.
   * Rejects and re-reads exactly as `addPackage` does.
   */
  updatePackage: (draft: PackageDraft, original: Package) => Promise<void>;
}

export const PackagesContext = createContext<PackagesState | null>(null);

export function usePackages(): PackagesState {
  const value = useContext(PackagesContext);
  if (!value) throw new Error('usePackages must be used inside PackagesProvider');
  return value;
}

/**
 * One package by id, or undefined.
 *
 * Undefined is a real answer rather than only a loading artefact, the same as `useWarehouse`: a
 * URL can name a package that has since been deleted, and the panel it opens degrades to closed
 * instead of rendering an empty sheet.
 */
export function usePackage(packageId: string | undefined): Package | undefined {
  const { packages } = usePackages();
  return packages.find((pkg) => pkg.id === packageId);
}
